import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { MY_TEACHER_NOTES_QUERY, START_NOTE_LESSON_MUTATION, NOTE_LESSON_QUERY } from '../query/gqlQueries.js'
import { cleanLesson } from '../lessons/buildTopicLesson'
import languages from '../languages'

// Notes a teacher has written about you, shown as homework. "Practise this"
// asks the server to write a short lesson aimed at exactly what the note says.
// That runs in the background on the Mac Studio — longer than a request may
// stay open through the tunnel — so this polls until it's ready, then opens it
// in the normal lesson view. The lesson is kept, so opening it again is instant.

const card = { background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.75rem 0.9rem' }
const btn = { background: '#1b1b21', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 9, padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }
const primary = { ...btn, background: '#34d399', color: '#06281d', border: 'none', fontWeight: 700 }
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '')
const POLL_MS = 4000
const GIVE_UP_MS = 9 * 60 * 1000

export default function TeacherNotesHomework({ onOpenLesson }) {
  const client = useApolloClient()
  const { data, error, refetch } = useQuery(MY_TEACHER_NOTES_QUERY, { variables: {}, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [startLesson] = useMutation(START_NOTE_LESSON_MUTATION)
  const [working, setWorking] = useState({}) // note id → status line
  const [failed, setFailed] = useState({})   // note id → message
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  const notes = data?.myTeacherNotes || []
  if (/Cannot query field|Unknown type/i.test(error?.message || '') || notes.length === 0) return null

  const open = (note, lessonData) => {
    try {
      const raw = JSON.parse(lessonData)
      const lesson = cleanLesson(raw, note.lang, languages[note.lang] || note.lang, raw.level)
      if (!lesson) throw new Error('The lesson came back empty.')
      onOpenLesson(lesson)
    } catch (e) {
      setFailed(f => ({ ...f, [note.id]: e.message || 'That lesson could not be opened.' }))
    }
  }

  const practise = async (note, fresh = false) => {
    setFailed(f => ({ ...f, [note.id]: null }))
    setWorking(w => ({ ...w, [note.id]: 'Writing a practice lesson from this note…' }))
    const started = Date.now()
    try {
      let state = (await startLesson({ variables: { id: note.id, fresh } })).data.startNoteLesson
      while (alive.current && state.status === 'generating' && Date.now() - started < GIVE_UP_MS) {
        await new Promise(r => setTimeout(r, POLL_MS))
        const secs = Math.round((Date.now() - started) / 1000)
        setWorking(w => ({ ...w, [note.id]: `Writing a practice lesson from this note… ${secs}s — this can take a couple of minutes.` }))
        state = (await client.query({ query: NOTE_LESSON_QUERY, variables: { id: note.id }, fetchPolicy: 'network-only' })).data.noteLesson
      }
      if (!alive.current) return
      if (state.status === 'ready' && state.lessonData) open(note, state.lessonData)
      else setFailed(f => ({ ...f, [note.id]: state.error || 'The lesson is taking too long — try again in a minute.' }))
    } catch (e) {
      setFailed(f => ({ ...f, [note.id]: e.message || 'Could not start the lesson.' }))
    }
    if (alive.current) { setWorking(w => ({ ...w, [note.id]: null })); refetch() }
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ color: '#8a8a95', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
        Notes from your teachers
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {notes.map(n => (
          <div key={n.id} style={card}>
            <div style={{ color: '#8a8a95', fontSize: '0.82rem', marginBottom: '0.3rem' }}>
              <b style={{ color: '#c3c3cc' }}>{n.teacher}</b> · {languages[n.lang] || n.lang} · {fmt(n.updatedAt || n.createdAt)}
            </div>
            <div style={{ color: '#e8e8ec', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.text}</div>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.6rem', flexWrap: 'wrap' }}>
              {working[n.id]
                ? <span style={{ color: '#6ee7b7', fontSize: '0.85rem' }}>{working[n.id]}</span>
                : <>
                  <button style={primary} onClick={() => practise(n)}>{n.lessonStatus === 'ready' ? 'Open practice' : 'Practise this'}</button>
                  {n.lessonStatus === 'ready' && <button style={btn} onClick={() => practise(n, true)}>Write a new one</button>}
                </>}
              {failed[n.id] && <span style={{ color: '#f87171', fontSize: '0.85rem' }}>{failed[n.id]}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
